/* eslint-disable complexity */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type { BillingInformation, Commerce, TagInfo, GigstackPortal } from '@interfaces/general';
import { useFetchCallback } from '@services/services';
import { isTransientAuthColdStart } from '@services/authReady';
import {
  MSG_GET_BILLING_ERROR,
  MSG_GET_ERROR,
  MSG_GET_GIGSTACK_ERROR,
  MSG_UPDATE_ERROR,
  MSG_UPDATE_OK,
} from '@pages/Commerce/messages';
import { DELIVERY_TYPE_MAP, ModuleName } from '@utils/constants';
import { MissingKeysOf } from '@interfaces/utils';
import { useCommerceActions, useCommerceFromStore } from '@store/actions/commerce';
import { countries } from '@utils/countries';
import useModules from '@utils/hooks/useModules';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { captureCommerceSaveError } from '@utils/sentry/captureOrderError';
import {
  anyActiveChannelAcceptsTransfer,
  channelHasPaymentMethod,
} from '@pages/Commerce/validations/deliveryChannelRules';

export type MissingCommerceKeys =
  | MissingKeysOf<Commerce>
  | `informacion_envio.${MissingKeysOf<Commerce['informacion_envio']>}`
  | `costo_por_porcentaje.${MissingKeysOf<Commerce['costo_por_porcentaje']>}`
  | `bank_transfer_details.${MissingKeysOf<Commerce['bank_transfer_details']>}`
  | `address.${MissingKeysOf<Commerce['address']>}`
  | 'tip_display_mode';

export const useGetCommerce = () => {
  const commerce = useCommerceFromStore();
  const { updateCommerce } = useCommerceActions();
  const { isFetching, call: fetchCommerce } = useFetchCallback<Commerce>('get', '/v1/comercio');
  const { hasModule } = useModules();
  const hasDeliveryByKm = hasModule(ModuleName.ENVIO_POR_KM);
  const hasDeliveryProviders = hasModule(ModuleName.PROVEEDORES_ENTREGA);

  useEffect(() => {
    if (!commerce.id) {
      fetchCommerce()
        .then((response) => {
          let newCommerce: Partial<Commerce> = response;
          if (!response.config_form_entrega) {
            if (hasDeliveryByKm && newCommerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.por_kilometro.key) {
              newCommerce = {
                ...newCommerce,
                config_form_entrega: {
                  calle: { required: false, show: false },
                  colonia: { required: false, show: false },
                  no_ext: { required: false, show: false },
                  no_int: { required: false, show: false },
                  ubicacion: { required: true, show: true },
                  referencias: { required: true, show: true, label: '' },
                },
              };
            } else if (
              hasDeliveryProviders &&
              newCommerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.delivery_providers.key
            ) {
              newCommerce = {
                ...newCommerce,
                config_form_entrega: {
                  calle: { required: false, show: false },
                  colonia: { required: false, show: false },
                  no_ext: { required: false, show: false },
                  no_int: { required: false, show: false },
                  ubicacion: { required: true, show: true },
                  referencias: { required: false, show: false, label: '' },
                },
              };
            } else {
              delete newCommerce.config_form_entrega;
            }
          }
          if (newCommerce.onboarding_finish_date)
            localStorage.setItem('onboarding_finish_date', newCommerce.onboarding_finish_date);
          // Asocia los eventos de Sentry al comercio activo (id no-PII) para poder filtrar
          // incidencias por comercio. Tolerante: si Sentry no está inicializado, no rompe.
          if (newCommerce.id) {
            try {
              Sentry.setUser({ id: String(newCommerce.id) });
            } catch {
              // off — la observabilidad no debe romper la app
            }
          }
          updateCommerce(newCommerce);
        })
        .catch((err) => {
          // Cold-start: el interceptor ya etiquetó el fallo transitorio de arranque y la data
          // carga sola al reintentar. No pintamos toast (mismo criterio que `useFetch`).
          if (isTransientAuthColdStart(err)) return;
          // `id` estable: este hook lo montan ~10 componentes a la vez (Layout, Header, NavBar,
          // MobileTopBar, BusinessClosedBanner, varias cards de Ajustes…). La request está
          // deduplicada en `useFetchCallback`, pero el rechazo llega a TODAS las instancias: sin
          // `id`, un único fallo pintaba una torre de toasts idénticos que tapaba la pantalla.
          toast.error(MSG_GET_ERROR, { id: 'get-commerce-error' });
        });
    }
  }, []);

  return { isFetching, commerce };
};

export const useEditCommerce = () => {
  const { commerce, isFetching } = useGetCommerce();
  const { updateCommerce } = useCommerceActions();
  const { isFetching: isUpdating, call: callUpdate } = useFetchCallback<Commerce, Commerce>('put', '/v1/comercio');

  // Resolves to `true` when the PUT persisted, `false` when it failed. It keeps swallowing the
  // rejection on purpose (several fire-and-forget callers — Header, BusinessClosedBanner — would
  // produce unhandled rejections otherwise), but callers that need to react to the failure now
  // have a signal: EditPageLayout pages must re-throw so the DirtyNavModal does not navigate.
  const handleUpdate = async (item: Commerce): Promise<boolean> => {
    try {
      await callUpdate({ data: item });
      updateCommerce({ ...item });
      toast.success(MSG_UPDATE_OK);
      return true;
    } catch (err) {
      toast.error(MSG_UPDATE_ERROR);
      return false;
    }
  };
  return { commerce, isFetching: isUpdating || isFetching, handleUpdate };
};

type CommerceDataValidator = (commerce: Commerce, requireBankTransferDetails?: boolean) => MissingCommerceKeys[];

export const validateCommerceDataValues: CommerceDataValidator = (commerce: Commerce) => {
  const missingValues: (keyof Commerce)[] = [];
  if (!commerce.nombre || commerce.nombre.length < 5 || commerce.nombre.length > 35) missingValues.push('nombre');
  if (!commerce.telefono) missingValues.push('telefono');
  if (!commerce.country_code) missingValues.push('country_code');
  const [country] = countries.filter((item) => item.countryCode === commerce.country_code);

  if (country && commerce.telefono?.toString().length !== country.phoneLength) missingValues.push('telefono');
  if (!commerce.direccion && !commerce.address?.fullAddress) {
    missingValues.push('direccion');
  }

  if (commerce.prefijo_folio && commerce.prefijo_folio.length > 5) {
    missingValues.push('prefijo_folio');
  }

  return missingValues;
};

export const validateDeliveryCommerceValues: CommerceDataValidator = (
  commerce: Commerce,
  requireBankTransferDetails = true,
) => {
  const missingValues: MissingCommerceKeys[] = [];
  if (!commerce.activar_envio_domicilio && !commerce.activar_comer_restaurant && !commerce.activar_pasar_recoger) {
    missingValues.push('activar_envio_domicilio');
    missingValues.push('activar_comer_restaurant');
    missingValues.push('activar_pasar_recoger');
  }
  // Delegates to the shared invariant (deliveryChannelRules.ts). Order preserved on purpose
  // (domicilio → comer_restaurant → pasar_recoger, NOT the DELIVERY_CHANNELS array order) so the
  // error summary UI keeps showing rows in the same order as before this refactor.
  if (commerce.activar_envio_domicilio && !channelHasPaymentMethod(commerce, 'activar_envio_domicilio'))
    missingValues.push('metodos_pago_envio_domicilio');
  if (commerce.activar_comer_restaurant && !channelHasPaymentMethod(commerce, 'activar_comer_restaurant'))
    missingValues.push('metodos_pago_comer_restaurant');
  if (commerce.activar_pasar_recoger && !channelHasPaymentMethod(commerce, 'activar_pasar_recoger'))
    missingValues.push('metodos_pago_pasar_recoger');
  // ── Home-delivery branches: only validated while the channel is ON ─────────────────────────
  // Every field below lives in a UI block that only mounts with `activar_envio_domicilio` on
  // (the "Costo de envío" and "Compra mínima" sections render just an InfoTip otherwise).
  // Validating them with the toggle off blocked the save on fields the user cannot even see:
  // the error summary pointed to a section without the field and the red badge had no way out.
  // Backend tolerates these keys empty (schema `required: []`, all nullable) and the diner menu
  // reads them behind the same toggle, so skipping them persists a coherent config.
  if (commerce.activar_envio_domicilio) {
    if (
      commerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.estandar.key &&
      !commerce.costo_envio_domicilio &&
      commerce.costo_envio_domicilio !== 0
    )
      missingValues.push('costo_envio_domicilio');
    if (
      commerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.por_colonia.key &&
      (!commerce.colonias ||
        !commerce.colonias.length ||
        commerce.colonias.some((col) => typeof col.costo !== 'number' || !col.nombre))
    )
      missingValues.push('colonias');

    // Mirrors `showEnvioGratisOption` in DeliveryDataV2: the free-shipping toggle (and its
    // amount) is hidden when an external provider handles the delivery fee.
    if (
      commerce.tipo_envio_domicilio !== DELIVERY_TYPE_MAP.delivery_providers.key &&
      commerce.activar_envio_gratis_compra_minima &&
      !commerce.importe_envio_gratis_compra_minima
    )
      missingValues.push('importe_envio_gratis_compra_minima');

    // Mirrors `showCompraMinimaOption`: hidden for free shipping and for external providers.
    if (
      commerce.tipo_envio_domicilio !== DELIVERY_TYPE_MAP.gratis.key &&
      commerce.tipo_envio_domicilio !== DELIVERY_TYPE_MAP.delivery_providers.key &&
      commerce.compra_minima_envio_domicilio === ''
    )
      missingValues.push('compra_minima_envio_domicilio');

    if (commerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.por_kilometro.key) {
      if (!commerce.informacion_envio?.costo_km || commerce.informacion_envio?.costo_km < 1) {
        missingValues.push('informacion_envio');
        missingValues.push('informacion_envio.costo_km');
      }
      if (
        (!commerce.informacion_envio?.min_km && commerce.informacion_envio?.min_km !== 0) ||
        commerce.informacion_envio?.min_km < 0
      ) {
        missingValues.push('informacion_envio');
        missingValues.push('informacion_envio.min_km');
      }
      if (!commerce.informacion_envio?.max_km || commerce.informacion_envio?.max_km < 1) {
        missingValues.push('informacion_envio');
        missingValues.push('informacion_envio.max_km');
      }
      if (
        (!commerce.costo_envio_domicilio && commerce.costo_envio_domicilio !== 0) ||
        commerce.costo_envio_domicilio < 0
      ) {
        missingValues.push('costo_envio_domicilio');
      }
    }

    if (commerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.percent.key) {
      if (!commerce.costo_por_porcentaje?.cantidad) {
        missingValues.push('costo_por_porcentaje');
      }
      if (commerce.costo_por_porcentaje?.cantidad && commerce?.costo_por_porcentaje?.cantidad > 100) {
        missingValues.push('costo_por_porcentaje');
      }
      if (commerce.costo_por_porcentaje?.cantidad && commerce?.costo_por_porcentaje?.cantidad < 1) {
        missingValues.push('costo_por_porcentaje');
      }
    }
  }

  // Bank details are required only when an ACTIVE channel accepts bank transfer. Gating by
  // `activar_envio_domicilio` alone would drop the other two channels (pickup / dine-in), which
  // also offer transfer; gating by nothing blocked the save on a leftover `transferencia: true`
  // of a channel that is switched off. Delegates to the shared invariant (deliveryChannelRules.ts).
  const transferOnActiveChannel = anyActiveChannelAcceptsTransfer(commerce);

  if (requireBankTransferDetails && transferOnActiveChannel) {
    if (!commerce.bank_transfer_details?.titular_name) {
      missingValues.push('bank_transfer_details');
      missingValues.push('bank_transfer_details.titular_name');
    }

    if (!commerce.bank_transfer_details?.clabe) {
      missingValues.push('bank_transfer_details');
      missingValues.push('bank_transfer_details.clabe');
    } else if (commerce.bank_transfer_details.clabe.length !== 18) {
      missingValues.push('bank_transfer_details');
      missingValues.push('bank_transfer_details.clabe');
    }

    if (!commerce.bank_transfer_details?.bank_name) {
      missingValues.push('bank_transfer_details');
      missingValues.push('bank_transfer_details.bank_name');
    }
  }

  if (
    commerce.activar_campo_agregar_propina &&
    (!commerce.tip_display_mode || !['percentage', 'fixed_amount'].includes(commerce.tip_display_mode))
  ) {
    missingValues.push('tip_display_mode' as MissingCommerceKeys);
  }

  return missingValues;
};

export const validateAddressValues: CommerceDataValidator = (commerce: Commerce) => {
  const missingValues: MissingCommerceKeys[] = [];
  if (!commerce.address?.city) missingValues.push('address.city');
  if (!commerce.address?.state) missingValues.push('address.state');
  if (!commerce.address?.postalCode) missingValues.push('address.postalCode');
  if (!commerce.address?.neighborhood) missingValues.push('address.neighborhood');
  if (!commerce.address?.exteriorNumber) missingValues.push('address.exteriorNumber');
  if (!commerce.address?.street) missingValues.push('address.street');
  if (!commerce.address?.fullAddress) missingValues.push('address.fullAddress');
  return missingValues;
};

// ── Saneo de NaN huérfanos ───────────────────────────────────────────────────
// La UI de tarifas permite dejar vacíos (NaN) los campos numéricos mientras se editan; el
// validador bloquea el guardado SOLO si la rama del campo está activa (p. ej. su tipo de
// envío). Si el usuario vacía un campo y luego cambia de rama, el NaN queda huérfano: el
// validador ya no lo ve y JSON.stringify(NaN) → null borraría el valor guardado en backend.
// Tras pasar la validación, cada NaN restante se restaura con el valor persistido; si no
// hay respaldo, la clave se omite del payload (undefined no se serializa).
const isOrphanNaN = (v: unknown): boolean => typeof v === 'number' && Number.isNaN(v);
const finiteOr = (candidate: unknown, fallback: number | undefined): number | undefined =>
  typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : fallback;

export const restoreOrphanNumericValues = (commerce: Commerce, saved?: Commerce | null): Commerce => {
  const next: Commerce = { ...commerce };

  if (isOrphanNaN(next.costo_envio_domicilio))
    next.costo_envio_domicilio = finiteOr(saved?.costo_envio_domicilio, undefined);
  if (isOrphanNaN(next.importe_envio_gratis_compra_minima))
    next.importe_envio_gratis_compra_minima = finiteOr(saved?.importe_envio_gratis_compra_minima, undefined);

  const info = next.informacion_envio;
  if (info && [info.min_km, info.costo_km, info.max_km].some(isOrphanNaN)) {
    const backup = saved?.informacion_envio;
    const min_km = isOrphanNaN(info.min_km) ? finiteOr(backup?.min_km, undefined) : info.min_km;
    const costo_km = isOrphanNaN(info.costo_km) ? finiteOr(backup?.costo_km, undefined) : info.costo_km;
    const max_km = isOrphanNaN(info.max_km) ? finiteOr(backup?.max_km, undefined) : info.max_km;
    // El tipo exige los 3 subcampos: si alguno quedó sin respaldo se omite el objeto entero.
    next.informacion_envio =
      min_km !== undefined && costo_km !== undefined && max_km !== undefined ? { min_km, costo_km, max_km } : undefined;
  }

  if (next.costo_por_porcentaje && isOrphanNaN(next.costo_por_porcentaje.cantidad)) {
    const backup = finiteOr(saved?.costo_por_porcentaje?.cantidad, undefined);
    next.costo_por_porcentaje = backup !== undefined ? { ...next.costo_por_porcentaje, cantidad: backup } : undefined;
  }

  return next;
};

export const useCommerce = (validator?: CommerceDataValidator, requireBankTransferDetails = true) => {
  const { isFetching } = useGetCommerce();
  const cachedCom = useCommerceFromStore();
  const { updateCommerce } = useCommerceActions();
  const [dirtyKeys, setDirtyKeys] = useState<Set<keyof Commerce>>(new Set());
  const [missingValues, setMissingValues] = useState<MissingCommerceKeys[]>([]);
  const [commerce, setCommerce] = useState(cachedCom);

  useEffect(() => {
    if (cachedCom)
      setCommerce((currentCommerce) => {
        const newCommerce = { ...cachedCom };
        dirtyKeys.forEach((key) => {
          (newCommerce[key] as Commerce[keyof Commerce]) = currentCommerce[key];
        });
        return newCommerce;
      });
  }, [cachedCom]);

  useEffect(() => {
    if (validator) {
      setMissingValues(validator(commerce, requireBankTransferDetails));
    }
  }, [commerce, validator, requireBankTransferDetails]);

  const { isFetching: isUpdating, call: callUpdate } = useFetchCallback<Commerce, Commerce>('put', '/v1/comercio');

  const handleUpdateValue = <K extends keyof Commerce>(key: K, value: Commerce[K]) => {
    setDirtyKeys((currentSet) => currentSet.add(key));
    setCommerce((oldCommerce) => {
      const newCommerce = { ...oldCommerce, [key]: value };
      if (key === 'activar_pago_enlace' && !value) {
        newCommerce.metodos_pago_comer_restaurant = {
          ...newCommerce.metodos_pago_comer_restaurant,
          enlace_pago: false,
        };
        newCommerce.metodos_pago_envio_domicilio = {
          ...newCommerce.metodos_pago_envio_domicilio,
          enlace_pago: false,
        };
        newCommerce.metodos_pago_pasar_recoger = {
          ...newCommerce.metodos_pago_pasar_recoger,
          enlace_pago: false,
        };
      }
      if (validator && missingValues.includes(key))
        setMissingValues(validator(newCommerce, requireBankTransferDetails));
      return newCommerce;
    });
  };

  const handleSubmitChanges = async (overrides?: Partial<Commerce>) => {
    const effectiveCommerce = overrides ? { ...commerce, ...overrides } : commerce;
    const effectiveDirtySize = dirtyKeys.size + (overrides ? Object.keys(overrides).length : 0);

    if (validator) {
      const values = validator(effectiveCommerce, requireBankTransferDetails);
      if (values.length) {
        setMissingValues(values);
        throw new Error('Missing values');
      }
    } else if (!effectiveDirtySize) throw new Error('No changes to submit');

    if (
      dirtyKeys.has('config_form_entrega') &&
      Object.values(effectiveCommerce.config_form_entrega).every((item) => !item.show)
    ) {
      toast.error('Al menos un valor debe estar habilitado');
      return;
    }

    const newCommerce = { ...effectiveCommerce };
    if (dirtyKeys.has('tipo_envio_domicilio')) {
      if (effectiveCommerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.por_kilometro.key) {
        newCommerce.config_form_entrega = {
          ...effectiveCommerce.config_form_entrega,
          ubicacion: { required: true, show: true },
        };
      } else if (effectiveCommerce.tipo_envio_domicilio === DELIVERY_TYPE_MAP.por_colonia.key) {
        newCommerce.config_form_entrega = {
          ...effectiveCommerce.config_form_entrega,
          colonia: { required: true, show: true },
        };
      }
    }
    if (dirtyKeys.has('bank_transfer_details') && !effectiveCommerce.bank_transfer_details?.clabe) {
      newCommerce.bank_transfer_details = null;
    }

    // La validación ya pasó: cualquier NaN restante es huérfano de una rama inactiva y no
    // debe llegar al PUT (JSON.stringify lo volvería null) ni quedarse en el store.
    const payload = restoreOrphanNumericValues(newCommerce, cachedCom);

    try {
      await callUpdate({ data: payload });
      updateCommerce({ ...payload });
      setDirtyKeys(new Set());
      toast.success(MSG_UPDATE_OK);
    } catch (err) {
      // Falla al guardar settings del comercio (incluye default_cook_time). Se reporta a
      // Sentry con los campos que el comercio intentaba guardar (dirtyKeys + overrides) para
      // saber QUÉ no se persistió. La captura centralizada de services.ts ya registró el HTTP;
      // esta añade el contexto de negocio del formulario.
      const dirtyKeyList = [...dirtyKeys, ...(overrides ? Object.keys(overrides) : [])].map(String);
      captureCommerceSaveError(err, {
        dirtyKeys: dirtyKeyList,
        http_status: isAxiosError(err) ? err.response?.status : undefined,
      });
      toast.error(MSG_UPDATE_ERROR);
      throw err;
    }
  };

  return {
    commerce,
    savedCommerce: cachedCom,
    isDirty: dirtyKeys.size > 0,
    isUpdating,
    isFetching,
    missingValues,
    handleUpdateValue,
    handleSubmitChanges,
  };
};

export const useGetTags = () => {
  const [tags, setTags] = useState<TagInfo[]>([]);
  const { isFetching, call: fetchTags } = useFetchCallback<TagInfo[]>('get', '/v1/tag');
  useEffect(() => {
    fetchTags().then((response) => {
      if (response) setTags([...response]);
    });
  }, []);
  return { isFetching, tags };
};

export const useGetTimezones = () => {
  const [timeZones, setTimeZones] = useState<{ label: string; value: string }[]>([]);
  const { isFetching, call: fetchTimeZones } = useFetchCallback<{ label: string; value: string }[]>(
    'get',
    '/v1/public/timezone',
  );
  useEffect(() => {
    fetchTimeZones().then((response) => {
      if (response) {
        const seen = new Set<string>();
        setTimeZones(response.filter((tz) => (seen.has(tz.value) ? false : seen.add(tz.value))));
      }
    });
  }, []);
  return { isFetching, timeZones };
};

export const useBillingConfig = () => {
  const { isFetching, call } = useFetchCallback<BillingInformation>('get', 'v1/account/billing');
  const { trackEvent } = useTracking();

  const handleBillingRequest = async () => {
    try {
      trackEvent(TRACK_EVENT_NAMES.BillingModificationRequested);
      const response = await call();
      window.open(response.stripe_customer_portal, '_self');
    } catch (error) {
      toast.error(MSG_GET_BILLING_ERROR);
    }
  };
  return { isFetching, handleBillingRequest };
};

export const useGigStackPortal = () => {
  const { isFetching, call } = useFetchCallback<GigstackPortal>('get', 'v1/account/gigstackportal');
  const { trackEvent } = useTracking();

  const handleGigStackPortalRequest = async () => {
    try {
      trackEvent(TRACK_EVENT_NAMES.GigstackPortalUrlRequested);
      const response = await call();
      window.open(response.signedUrl, '_self');
    } catch (error) {
      Sentry.captureMessage(MSG_GET_GIGSTACK_ERROR);
      Sentry.captureException(error);
      toast.error(MSG_GET_GIGSTACK_ERROR);
    }
  };

  return {
    isFetching,
    handleGigStackPortalRequest,
  };
};

export const isAddressComplete = (commerce: Commerce): boolean => {
  return Boolean(
    commerce?.address?.fullAddress &&
      commerce?.address?.latitude &&
      commerce?.address?.longitude &&
      commerce?.address?.state &&
      commerce?.address?.city &&
      commerce?.address?.street &&
      commerce?.address?.postalCode,
  );
};

export const useCommerceUIActions = () => {
  const { updateCommerce } = useCommerceActions();
  const { call: setCommerceLogo } = useFetchCallback<{ logotipo: string | null }>('put', '/v1/comercio');
  const { hasModule } = useModules();

  const handleCommerceLogoUpdate = async (url: string) => {
    await setCommerceLogo({ data: { logotipo: url } });
    updateCommerce({ logotipo: url });
  };

  const handleCommerceLogoRemove = async () => {
    await setCommerceLogo({ data: { logotipo: null } });
    updateCommerce({ logotipo: null });
  };

  const handleCommercePixelToggle = (isEditingPixel: boolean, setIsEditingPixel: (value: boolean) => void) => {
    if (hasModule(ModuleName.SEGUIMIENTO_REDES_SOCIALES)) {
      if (isEditingPixel) {
        setIsEditingPixel(false);
        toast.success('Pixel de Facebook actualizado');
      } else {
        setIsEditingPixel(true);
      }
    }
  };

  const handleCommerceCoverUpdate = (
    handleUpdateValue: <K extends keyof Commerce>(field: K, value: Commerce[K]) => void,
    url: string,
  ) => {
    // Only updates local state — the save happens when the user clicks GUARDAR.
    // Do not show a success toast here to avoid implying the change was persisted.
    handleUpdateValue('cover' as keyof Commerce, url);
  };

  const handleCommerceCoverRemove = (
    handleUpdateValue: <K extends keyof Commerce>(field: K, value: Commerce[K]) => void,
  ) => {
    // Only updates local state — no success toast to avoid premature feedback.
    handleUpdateValue('cover' as keyof Commerce, '');
  };

  return {
    handleCommerceLogoUpdate,
    handleCommerceLogoRemove,
    handleCommercePixelToggle,
    handleCommerceCoverUpdate,
    handleCommerceCoverRemove,
  };
};
